/**
 * Shared error-handling utilities used across auth pages and hooks.
 */

/** Returns true when the error is a network / connectivity failure (server down, offline, etc.) */
export function isNetworkError(error: unknown): boolean {
  if (error instanceof TypeError && error.message === "Failed to fetch")
    return true;
  if (error instanceof DOMException && error.name === "AbortError") return true;
  return false;
}

/**
 * Maps Better Auth error messages to user-friendly strings.
 *
 * Better Auth returns `{ data, error }` where `error.message` corresponds to
 * one of the BASE_ERROR_CODES strings from `@better-auth/core`.
 */
const AUTH_ERROR_MAP: Record<string, string> = {
  "Invalid email or password":
    "The email or password you entered is incorrect. Please try again.",
  "Invalid email": "Please enter a valid email address.",
  "Invalid password": "The password you entered is incorrect.",
  "User not found": "No account found with this email address.",
  "User already exists.": "An account with this email already exists.",
  "User already exists. Use another email.":
    "An account with this email already exists. Please use a different email.",
  "Password too short": "Password must be at least 8 characters long.",
  "Password too long": "Password is too long.",
  "Invalid token":
    "This link has expired or is invalid. Please request a new one.",
  "Session expired. Re-authenticate to perform this action.":
    "Your session has expired. Please sign in again.",
  "Email not verified": "Please verify your email address before signing in.",
  "Provider not found":
    "This sign-in method is not configured. Please contact an administrator.",
};

export function friendlyAuthError(
  error: { message?: string; status?: number; statusText?: string } | null,
): string {
  if (!error) return "An unexpected error occurred.";

  const mapped = AUTH_ERROR_MAP[error.message ?? ""];
  if (mapped) return mapped;

  // Use the original message if it's short enough to be human-readable
  if (error.message && error.message.length < 200) return error.message;
  return "Authentication failed. Please try again.";
}

export function friendlyNetworkError(): string {
  return "Unable to connect to the server. Please check your internet connection or try again later.";
}

export const GENERIC_MUTATION_ERROR_MESSAGE =
  "We couldn't save your changes. Please check your connection and try again.";

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "";
}

export function friendlyMutationError(error: unknown): string {
  if (
    typeof window !== "undefined" &&
    typeof navigator !== "undefined" &&
    !navigator.onLine
  ) {
    return GENERIC_MUTATION_ERROR_MESSAGE;
  }

  if (isNetworkError(error)) {
    return GENERIC_MUTATION_ERROR_MESSAGE;
  }

  const message = errorMessage(error);
  const normalized = message.toLowerCase();

  if (
    normalized.includes("failed to fetch") ||
    normalized.includes("networkerror") ||
    normalized.includes("network error") ||
    normalized.includes("offline") ||
    normalized.includes("disconnected") ||
    normalized.includes("not connected") ||
    (normalized.includes("zero") && normalized.includes("mutation"))
  ) {
    return GENERIC_MUTATION_ERROR_MESSAGE;
  }

  return message || "Something went wrong. Please try again.";
}

/**
 * A mutation failure with a user-facing message. `expected` marks failures
 * that are not bugs (offline, lost sync connection, permission refused), so
 * they are not reported to Sentry.
 */
export class MutationError extends Error {
  readonly expected: boolean;

  constructor(message: string, expected: boolean) {
    super(message);
    this.name = "MutationError";
    this.expected = expected;
  }
}

function isPermissionError(error: unknown): boolean {
  return errorMessage(error).startsWith("Not allowed to");
}

export function toFriendlyMutationError(error: unknown): MutationError {
  const message = friendlyMutationError(error);
  return new MutationError(
    message,
    message === GENERIC_MUTATION_ERROR_MESSAGE || isPermissionError(error),
  );
}
